import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import SchoolIcon from "@mui/icons-material/School";
import SecurityIcon from "@mui/icons-material/Security";
import StorageIcon from "@mui/icons-material/Storage";
import CodeIcon from "@mui/icons-material/Code";
import BugReportIcon from "@mui/icons-material/BugReport";
import ShieldIcon from "@mui/icons-material/Shield";
import GavelIcon from "@mui/icons-material/Gavel";
import MemoryIcon from "@mui/icons-material/Memory";
import LanIcon from "@mui/icons-material/Lan";
import CloudIcon from "@mui/icons-material/Cloud";
import BuildIcon from "@mui/icons-material/Build";
import WorkIcon from "@mui/icons-material/Work";
import {
  FiCalendar,
  FiMapPin,
  FiArrowRight,
  FiArrowUp,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import "../styles/Experience.css";

/* ── Timeline data ── */
const TIMELINE = [
  {
    id:       "msc",
    type:     "edu",
    icon:     SchoolIcon,
    title:    "MSc Cybersecurity",
    org:      "National College of Ireland",
    location: "Dublin",
    period:   "Sept 2025 – Present",
    tag:      "MSc",
    tagCls:   "tag-edu",
    preview:  "Advanced programme covering offensive security, cloud architecture, AI/ML security, and cyber governance.",
    groups: [
      {
        label: "Offensive Security",
        Icon:  BugReportIcon,
        modules: ["Network Security & Penetration Testing", "Malware Analysis", "Secure Web Development"],
      },
      {
        label: "Defensive Security",
        Icon:  ShieldIcon,
        modules: ["Security Fundamentals", "Cloud Architecture & Security", "AI/ML in Cybersecurity", "Business Resilience & Incident Management"],
      },
      {
        label: "Governance & Risk",
        Icon:  GavelIcon,
        modules: ["Cryptography & Blockchain", "Data Governance, Ethics & Sustainability"],
      },
    ],
  },
  {
    id:       "atos",
    type:     "work",
    icon:     SecurityIcon,
    title:    "IT Support Analyst II",
    org:      "Atos",
    location: "Dundrum, Dublin",
    period:   "May 2025 – Apr 2026",
    tag:      "Security",
    tagCls:   "tag-cyber",
    preview:  "Security triage and incident response across M365 and Azure AD environments.",
    bullets: [
      "Triaged security signals across M365 & Azure AD for credential misuse",
      "Investigated anomalous PowerShell activity and endpoint alerts",
      "Coordinated incident response between IT operations and engineering",
    ],
  },
  {
    id:       "aws",
    type:     "work",
    icon:     StorageIcon,
    title:    "IT Hardware Technician",
    org:      "Amazon Web Services",
    location: "Tallaght, Dublin",
    period:   "Feb 2024 – Apr 2025",
    tag:      "Cloud",
    tagCls:   "tag-cloud",
    preview:  "Maintained secure cloud infrastructure hardware in a high-availability data centre.",
    bullets: [
      "Maintained cloud infrastructure hardware and access controls",
      "Performed firmware updates and vulnerability mitigation",
      "Ensured high-availability per global compliance standards",
    ],
  },
  {
    id:       "surecom",
    type:     "work",
    icon:     SecurityIcon,
    title:    "IT Security Support Analyst",
    org:      "Surecom",
    location: "Rathcoole, Dublin",
    period:   "Jan 2022 – Jan 2023",
    tag:      "Security",
    tagCls:   "tag-cyber",
    preview:  "Vulnerability scanning, traffic analysis, and ISO 27001 audit support.",
    bullets: [
      "Conducted vulnerability scans using Nessus & Nmap",
      "Analysed traffic via Wireshark to validate IoCs",
      "Supported ISO 27001 audits and GDPR compliance",
    ],
  },
  {
    id:       "bsc",
    type:     "edu",
    icon:     CodeIcon,
    title:    "BSc Computer Science",
    org:      "Technological University Dublin",
    location: "Dublin",
    period:   "Aug 2019 – May 2023",
    tag:      "BSc",
    tagCls:   "tag-edu",
    preview:  "Four-year programme spanning programming, systems, networking, cloud, and advanced security.",
    groups: [
      { label: "Core Computing", Icon: BuildIcon,   modules: ["IT Fundamentals", "Mathematics I & II", "Data Exploration", "Team Robotic Computing"] },
      { label: "Programming",    Icon: CodeIcon,    modules: ["OOP (Java, Python)", "Algorithms", "Systems Software"] },
      { label: "Systems",        Icon: MemoryIcon,  modules: ["Operating Systems", "Microprocessors", "Architecture"] },
      { label: "Networking",     Icon: LanIcon,     modules: ["Networking 1–3", "Network Programming"] },
      { label: "Cloud & DevOps", Icon: CloudIcon,   modules: ["Cloud Computing", "DevOps"] },
      { label: "Security",       Icon: ShieldIcon,  modules: ["Advanced Security I & II"] },
    ],
  },
];

const NAV_SECTIONS = TIMELINE.map((t) => ({ id: t.id, label: t.org.split(" ")[0] }));

/* ── Single experience card ── */
function ExpCard({ item, index }) {
  const [open, setOpen] = useState(false);
  const TypeIcon = item.icon;
  const isWork   = item.type === "work";

  return (
    <div
      className={`exp-card ${open ? "is-open" : ""}`}
      style={{ "--card-idx": index }}
      id={item.id}
    >
      {/* ── Left accent bar ── */}
      <div className={`exp-bar ${isWork ? "bar-work" : "bar-edu"}`} />

      {/* ── Icon blob ── */}
      <div className={`exp-icon-wrap ${isWork ? "icon-work" : "icon-edu"}`}>
        <TypeIcon style={{ fontSize: 20 }} />
      </div>

      {/* ── Content ── */}
      <div className="exp-content">

        {/* Header row */}
        <div className="exp-header">
          <div className="exp-header-left">
            <span className={`exp-tag ${item.tagCls}`}>{item.tag}</span>
            <h3 className="exp-title">{item.title}</h3>
            <p className="exp-org">{item.org}</p>
          </div>
          <div className="exp-header-right">
            <span className="exp-meta-item">
              <FiCalendar size={11} /> {item.period}
            </span>
            <span className="exp-meta-item">
              <FiMapPin size={11} /> {item.location}
            </span>
          </div>
        </div>

        {/* Preview */}
        <p className="exp-preview">{item.preview}</p>

        {/* Expandable body */}
        <div className={`exp-body ${open ? "exp-body--open" : ""}`}>

          {/* Work bullets */}
          {item.bullets && (
            <ul className="exp-bullets">
              {item.bullets.map((b, i) => (
                <li key={i}>
                  <FiArrowRight size={12} className="bullet-icon" />
                  {b}
                </li>
              ))}
            </ul>
          )}

          {/* Education module groups */}
          {item.groups && (
            <div className="exp-groups">
              {item.groups.map(({ label, Icon: GIcon, modules }) => (
                <div key={label} className="exp-group">
                  <h4 className="exp-group-label">
                    <span className="exp-group-icon">
                      <GIcon style={{ fontSize: 13 }} />
                    </span>
                    {label}
                  </h4>
                  <div className="exp-modules-grid">
                    {modules.map((m) => (
                      <span key={m} className="exp-module-chip">{m}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toggle */}
        <button className="exp-toggle" onClick={() => setOpen((p) => !p)}>
          {open ? (
            <><FiChevronUp size={13} /> Show less</>
          ) : (
            <>Read more <FiChevronDown size={13} /></>
          )}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
function Experience() {
  const navigate = useNavigate();
  const [activeSection,  setActiveSection]  = useState("msc");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [sectionLabel,   setSectionLabel]   = useState("");
  const [showLabel,      setShowLabel]      = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id    = entry.target.id;
            const found = NAV_SECTIONS.find((s) => s.id === id);
            setActiveSection(id);
            setSectionLabel(found?.label || id);
            setShowLabel(true);
            setTimeout(() => setShowLabel(false), 1200);
          }
        });
      },
      { threshold: 0.35 }
    );
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handle = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="exp-page">
      <Helmet>
        <title>Experience | Biko — Software Engineer & Security Analyst Dublin</title>
        <meta
          name="description"
          content="Career history of Biko — IT Security Analyst at Atos, Hardware Technician at AWS, MSc Cybersecurity at NCI Dublin, BSc Computer Science at TU Dublin."
        />
        <meta property="og:title" content="Experience | Biko Portfolio" />
        <meta property="og:url"   content="https://bikosulu.com/experience" />
      </Helmet>

      {/* Scroll progress */}
      <div className="exp-scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Section popup */}
      {showLabel && <div className="exp-section-popup">{sectionLabel}</div>}

      {/* Dot nav */}
      <nav className="exp-scroll-nav" aria-label="Timeline sections">
        {NAV_SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            className={`exp-nav-dot ${activeSection === id ? "active" : ""}`}
            onClick={() => scrollTo(id)}
            data-tooltip={label}
            aria-label={`Go to ${label}`}
          />
        ))}
      </nav>

      <div className="exp-page-inner">

        {/* ── Page nav toggle ── */}
        <nav className="page-nav-toggle">
          <button className="page-nav-btn" onClick={() => navigate("/")}>
            <HomeIcon style={{ fontSize: 14 }} /> Home
          </button>
          <span className="page-nav-sep">/</span>
          <span className="page-nav-current">Experience</span>
        </nav>

        {/* ── Header ── */}
        <div className="exp-page-header">
          <span className="exp-page-eyebrow">Career</span>
          <h1 className="exp-page-title">
            Professional <span>Roadmap</span>
          </h1>
          <p className="exp-page-subtitle">
            From BSc to MSc — security analysis, cloud infrastructure, and full-stack engineering across Dublin.
          </p>

          {/* Legend */}
          <div className="exp-legend">
            <span className="legend-item">
              <span className="legend-dot dot-work" />
              <WorkIcon style={{ fontSize: 13 }} /> Work
            </span>
            <span className="legend-item">
              <span className="legend-dot dot-edu" />
              <SchoolIcon style={{ fontSize: 13 }} /> Education
            </span>
          </div>
        </div>

        {/* ── Cards ── */}
        <div className="exp-cards">
          {TIMELINE.map((item, idx) => (
            <ExpCard key={item.id} item={item} index={idx} />
          ))}
        </div>

        {/* ── Footer CTA ── */}
        <div className="exp-footer-cta">
          <button className="cta-outline" onClick={() => navigate("/")}>
            <FiArrowUp size={13} /> Back to Home
          </button>
        </div>

      </div>
    </div>
  );
}

export default Experience;